import { useEffect, useState } from 'react'
import { theme } from '../theme'
import { api } from '../api'
import type { Me, AccountSharing as Sharing, AccountRole, InvitePreview } from '../types'

const ROLE_OPTIONS: { value: AccountRole; label: string; hint: string }[] = [
  { value: 'manager', label: 'Manager', hint: 'Full access, including settings. Cannot manage your sharing or billing.' },
  { value: 'editor',  label: 'Editor',  hint: 'Can edit your plan, recipes, pantry — but not your settings.' },
  { value: 'viewer',  label: 'Viewer',  hint: 'Read-only. Can see your plan and lists, can’t change anything.' },
]

export function roleLabel(r: AccountRole): string {
  return ROLE_OPTIONS.find(o => o.value === r)?.label ?? r
}

// reload after switching accounts so all data + the SSE subscription re-resolve
// to the new effective account (the server keys both off the session).
// Exported so the user-chip avatar menu (Shell.tsx) can offer "Return to my
// account" through the same single code path the inline sharing panel uses.
export function switchTo(userId: number | null) {
  api.actAs(userId).then(() => { window.location.href = '/plan' }).catch(e => console.error(e))
}

const btn = (primary: boolean): React.CSSProperties => ({
  all: 'unset', cursor: 'pointer',
  padding: '8px 14px', borderRadius: theme.radius,
  border: `1px solid ${primary ? theme.ink : theme.line}`,
  background: primary ? theme.ink : theme.surface,
  color: primary ? theme.surface : theme.ink,
  fontFamily: theme.sans, fontSize: 13, fontWeight: 600,
})

// AccountSharing is the owner's delegated-access management surface: switch into
// accounts shared with you, and grant/revoke access to your own account. While
// you're acting-as another account, management is hidden (it's gated server-side
// to your own account); only the "return home" path is offered.
export function AccountSharing({ me }: { me: Me }) {
  const acting = me.session?.acting_as ?? null
  const accounts = me.session?.available_accounts ?? []
  const others = accounts.filter(a => a.role !== 'owner')

  const [sharing, setSharing] = useState<Sharing | null>(null)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<AccountRole>('manager')
  const [busy, setBusy] = useState(false)
  const [link, setLink] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)

  const reload = () => api.account().then(setSharing).catch(() => setSharing({ grants: [], invites: [] }))
  useEffect(() => { if (!acting) reload() }, [acting])

  if (acting) {
    return (
      <div style={{ fontSize: 14, color: theme.inkSoft }}>
        You’re managing <strong style={{ color: theme.ink }}>{acting.name}</strong>’s account.
        Return to your own account to manage sharing.
        <div style={{ marginTop: 12 }}>
          <button style={btn(true)} onClick={() => switchTo(null)}>Return to my account</button>
        </div>
      </div>
    )
  }

  const submitInvite = async () => {
    const e = email.trim()
    if (!e || busy) return
    setBusy(true); setLink(null); setNote(null)
    try {
      const res = await api.inviteToAccount(e, role)
      if (res.granted) {
        setNote(`${res.grantee_name ?? e} now has ${roleLabel(role)} access.`)
      } else if (res.token) {
        setLink(`${window.location.origin}/grant/${res.token}`)
        setNote(`Invite created. Send ${e} this link to accept:`)
      }
      setEmail('')
      reload()
    } catch (err) {
      setNote('Could not create the invite.')
      console.error(err)
    } finally {
      setBusy(false)
    }
  }

  const revokeGrant = async (id: number) => {
    await api.revokeAccountGrant(id).catch(e => console.error(e))
    reload()
  }
  const revokeInvite = async (id: number) => {
    await api.revokeAccountInvite(id).catch(e => console.error(e))
    reload()
  }

  const inputStyle: React.CSSProperties = {
    flex: 1, minWidth: 160, boxSizing: 'border-box',
    padding: '9px 12px', borderRadius: theme.radius,
    border: `1px solid ${theme.line}`, background: theme.surface,
    color: theme.ink, fontFamily: theme.sans, fontSize: 14,
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Accounts shared WITH you */}
      {others.length > 0 && (
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: theme.ink, marginBottom: 8 }}>
            Accounts you can manage
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {others.map(a => (
              <div key={a.id} style={rowStyle}>
                <Avatar name={a.name} picture={a.picture} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, color: theme.ink, fontWeight: 600 }}>{a.name}</div>
                  <div style={{ fontSize: 12, color: theme.muted }}>{roleLabel(a.role)} access</div>
                </div>
                <button style={btn(true)} onClick={() => switchTo(a.id)}>Manage</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Grant access to YOUR account */}
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: theme.ink, marginBottom: 4 }}>
          Invite someone to your account
        </div>
        <div style={{ fontSize: 13, color: theme.inkSoft, marginBottom: 10 }}>
          They sign in with their own Google account and manage <em>your</em> kitchen — your plan,
          recipes, pantry, and credits. You can change their role or revoke access anytime.
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <input
            type="email" value={email} placeholder="name@example.com"
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') submitInvite() }}
            style={inputStyle}
          />
          <select
            value={role} onChange={e => setRole(e.target.value as AccountRole)}
            style={{ ...inputStyle, flex: '0 0 auto', minWidth: 0, cursor: 'pointer' }}
          >
            {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button style={btn(true)} onClick={submitInvite} disabled={busy}>
            {busy ? 'Inviting…' : 'Invite'}
          </button>
        </div>
        <div style={{ fontSize: 12, color: theme.muted, marginTop: 6 }}>
          {ROLE_OPTIONS.find(o => o.value === role)?.hint}
        </div>
        {note && <div style={{ fontSize: 13, color: theme.ink, marginTop: 10 }}>{note}</div>}
        {link && (
          <div style={{
            marginTop: 6, padding: '8px 10px', borderRadius: theme.radius,
            border: `1px solid ${theme.line}`, background: theme.bg,
            fontFamily: 'monospace', fontSize: 12, color: theme.ink, wordBreak: 'break-all',
          }}>
            {link}
          </div>
        )}
      </div>

      {/* People with access */}
      {sharing && sharing.grants.length > 0 && (
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: theme.ink, marginBottom: 8 }}>
            People with access
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sharing.grants.map(g => (
              <div key={g.id} style={rowStyle}>
                <Avatar name={g.grantee_name} picture={g.grantee_picture} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, color: theme.ink, fontWeight: 600 }}>{g.grantee_name}</div>
                  <div style={{ fontSize: 12, color: theme.muted }}>{g.grantee_email} · {roleLabel(g.role)}</div>
                </div>
                <button style={btn(false)} onClick={() => revokeGrant(g.id)}>Revoke</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending invites */}
      {sharing && sharing.invites.length > 0 && (
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: theme.ink, marginBottom: 8 }}>
            Pending invites
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sharing.invites.map(iv => (
              <div key={iv.id} style={rowStyle}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, color: theme.ink }}>{iv.email}</div>
                  <div style={{ fontSize: 12, color: theme.muted }}>{roleLabel(iv.role)} · awaiting sign-in</div>
                </div>
                <button
                  style={btn(false)}
                  onClick={() => navigator.clipboard?.writeText(`${window.location.origin}/grant/${iv.token}`)}
                >Copy link</button>
                <button style={btn(false)} onClick={() => revokeInvite(iv.id)}>Cancel</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// The always-on managing-another-account indicator is no longer a full-bleed
// banner. It now lives inside the user-chip avatar (Shell.tsx): the managed
// account's photo with the real user's photo as a small corner badge, an accent
// ring, and a "Return to my account" row in the avatar menu — all driven by the
// exported switchTo/roleLabel helpers above. This keeps the reminder always on
// screen but out of the way, in the two corners the avatar already occupies.

// GrantAcceptPage is the /grant/<token> landing. A logged-in user whose verified
// email matches the invite can accept, which creates the grant and lands them
// on the now-shared account in their switcher.
export function GrantAcceptPage({ token }: { token: string }) {
  const [preview, setPreview] = useState<InvitePreview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  useEffect(() => {
    api.invitePreview(token).then(setPreview).catch(() => setError('This invite link is invalid or has expired.'))
  }, [token])

  const accept = async () => {
    setBusy(true)
    try {
      await api.acceptInvite(token)
      setDone(true)
      setTimeout(() => { switchTo(null); window.location.href = '/plan' }, 800)
    } catch (e) {
      setError('Could not accept this invite. Make sure you’re signed in with the invited email.')
      console.error(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{
      minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24, background: theme.bg, fontFamily: theme.sans,
    }}>
      <div style={{
        maxWidth: 440, width: '100%', background: theme.surface,
        border: theme.cardBorder, borderRadius: theme.radiusLg, padding: 28,
      }}>
        <div style={{
          fontFamily: theme.serif, fontWeight: theme.headWeight, fontSize: 26,
          color: theme.ink, marginBottom: 12,
        }}>Account sharing</div>

        {error && <div style={{ fontSize: 14, color: theme.accent }}>{error}</div>}

        {!error && !preview && <div style={{ fontSize: 14, color: theme.inkSoft }}>Loading…</div>}

        {!error && preview && (
          done ? (
            <div style={{ fontSize: 15, color: theme.ink }}>
              You now have access to <strong>{preview.owner_name}</strong>’s account. Taking you there…
            </div>
          ) : (
            <>
              <div style={{ fontSize: 15, color: theme.ink, lineHeight: 1.5 }}>
                <strong>{preview.owner_name}</strong> invited you to manage their Mila account
                as <strong>{roleLabel(preview.role)}</strong>.
              </div>
              {!preview.email_matches && (
                <div style={{ fontSize: 13, color: theme.accent, marginTop: 12 }}>
                  This invite was sent to {preview.email}. Sign in with that account to accept.
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <button
                  style={{ ...btn(true), opacity: preview.email_matches && !busy ? 1 : 0.5 }}
                  onClick={accept}
                  disabled={!preview.email_matches || busy}
                >{busy ? 'Accepting…' : 'Accept'}</button>
                <button style={btn(false)} onClick={() => { window.location.href = '/plan' }}>Not now</button>
              </div>
            </>
          )
        )}
      </div>
    </div>
  )
}

const rowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '10px 12px', borderRadius: theme.radius,
  border: `1px solid ${theme.line}`, background: theme.surface,
}

function Avatar({ name, picture }: { name: string; picture: string }) {
  const initial = (name || '?').trim().charAt(0).toUpperCase()
  if (picture) {
    return <img src={picture} alt="" width={32} height={32} style={{ borderRadius: '50%', flexShrink: 0 }} />
  }
  return (
    <div style={{
      width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
      background: theme.ink, color: theme.surface,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: theme.sans, fontSize: 14, fontWeight: 600,
    }}>{initial}</div>
  )
}
